import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { DashboardCard } from "@dashboard/components/Card";
import { type ConfirmButtonTransitionState } from "@dashboard/components/ConfirmButton/ConfirmButton";
import { DetailPageLayout } from "@dashboard/components/Layouts/Detail";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { Savebar } from "@dashboard/components/Savebar";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import {
  AtticCardTier,
  type AtticErrorFragment,
  type AtticLocationDetailsFragment,
  AtticLocationKind,
  type AtticLocationRuleChoicesQuery,
} from "@dashboard/graphql";
import useNavigator from "@dashboard/hooks/useNavigator";
import { MONO_FONT_FAMILY } from "@dashboard/styles/monoFontFamily";
import { TableBody, TableCell } from "@material-ui/core";
import {
  Box,
  Checkbox,
  DynamicMultiselect,
  Input,
  type Option,
  Select,
  Text,
} from "@saleor/macaw-ui-next";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { Link } from "react-router-dom";

import { getAtticInventoryErrorMessage, getAtticInventoryFieldError } from "../../errors";
import { kindMessages, locationMessages, sectionMessages, tierMessages } from "../../messages";
import { AtticInventoryDestinationIcon } from "../../topNav";
import { atticLocationListPath, atticLocationUrl } from "../../urls";
import { AtticUnitsTable } from "../AtticUnitsTable/AtticUnitsTable";

export interface AtticLocationFormData {
  code: string;
  name: string;
  kind: AtticLocationKind;
  /** A parent's code; "" for top level. */
  parent: string;
  isActive: boolean;
  notes: string;
  /** Rules: what the location takes; empty = any. */
  cardCategories: string[];
  setCodes: string[];
  rarities: string[];
  finishes: string[];
  tiers: AtticCardTier[];
}

export const emptyLocationForm = (): AtticLocationFormData => ({
  code: "",
  name: "",
  kind: AtticLocationKind.OTHER,
  parent: "",
  isActive: true,
  notes: "",
  cardCategories: [],
  setCodes: [],
  rarities: [],
  finishes: [],
  tiers: [],
});

export const locationToFormData = (
  location: AtticLocationDetailsFragment,
): AtticLocationFormData => ({
  code: location.code,
  name: location.name,
  kind: location.kind,
  parent: location.parent?.code ?? "",
  isActive: location.isActive,
  notes: location.notes,
  cardCategories: location.cardCategories,
  setCodes: location.setCodes,
  rarities: location.rarities,
  finishes: location.finishes,
  tiers: location.tiers,
});

/** The values a rule can name, from the catalog; null while loading. */
export type AtticLocationRuleChoices =
  | AtticLocationRuleChoicesQuery["atticLocationRuleChoices"]
  | null;

export interface AtticLocationDetailsPageProps {
  /** null on the create page. */
  location: AtticLocationDetailsFragment | null;
  choices: AtticLocationRuleChoices;
  errors: AtticErrorFragment[];
  disabled: boolean;
  saveButtonBarState: ConfirmButtonTransitionState;
  onSubmit: (data: AtticLocationFormData) => void;
  /** Opens the delete dialog; absent on the create page. */
  onDelete?: () => void;
}

/** Deletable: an existing, non-holding location with nothing in it. */
export const isLocationEmpty = (location: AtticLocationDetailsFragment): boolean =>
  location.kind !== AtticLocationKind.HOLDING &&
  location.unitCount === 0 &&
  location.children.length === 0;

const FIELDS = ["code", "name", "kind", "parent", "isActive", "notes"];

/** A rule's picker: the catalog's values, plus whatever the row already holds. */
const RulePicker = ({
  label,
  values,
  options,
  disabled,
  onChange,
  testId,
}: {
  label: string;
  values: string[];
  options: Option[];
  disabled: boolean;
  onChange: (values: string[]) => void;
  testId: string;
}) => {
  const [typed, setTyped] = useState("");
  const known = new Map(options.map(option => [option.value, option]));
  const all = [
    ...options,
    ...values.filter(value => !known.has(value)).map(value => ({ label: value, value })),
  ];
  const shown = typed
    ? all.filter(option => option.label.toLowerCase().includes(typed.toLowerCase()))
    : all;

  return (
    <DynamicMultiselect
      label={label}
      options={shown}
      value={values.map(value => known.get(value) ?? { label: value, value })}
      disabled={disabled}
      onChange={picked => onChange(picked.map(option => option.value))}
      onInputValueChange={setTyped}
      data-test-id={testId}
    />
  );
};

export const AtticLocationDetailsPage = ({
  location,
  choices,
  errors,
  disabled,
  saveButtonBarState,
  onSubmit,
  onDelete,
}: AtticLocationDetailsPageProps) => {
  const intl = useIntl();
  const navigate = useNavigator();
  const [data, setData] = useState<AtticLocationFormData>(
    location ? locationToFormData(location) : emptyLocationForm(),
  );

  useEffect(() => {
    if (location) {
      setData(locationToFormData(location));
    }
  }, [location]);

  const change = (patch: Partial<AtticLocationFormData>) =>
    setData(current => ({ ...current, ...patch }));
  const kindOptions = Object.values(AtticLocationKind).map(kind => ({
    label: intl.formatMessage(kindMessages[kind]),
    value: kind,
  }));
  const fieldError = (field: string) =>
    getAtticInventoryErrorMessage(getAtticInventoryFieldError(errors, field), intl);
  const formErrors = errors
    .filter(error => !error.field || !FIELDS.includes(error.field))
    .map(error => getAtticInventoryErrorMessage(error, intl));

  return (
    <DetailPageLayout>
      <TopNav
        href={atticLocationListPath}
        hrefIcon={<AtticInventoryDestinationIcon />}
        hrefTitle={intl.formatMessage(sectionMessages.locations)}
        title={location ? location.code : intl.formatMessage(locationMessages.createTitle)}
      />
      <DetailPageLayout.Content>
        <DashboardCard data-test-id="attic-location-form">
          <DashboardCard.Header>
            <DashboardCard.Title>
              {intl.formatMessage(locationMessages.detailsTitle)}
            </DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            {formErrors.map(message => (
              <Text
                key={message}
                as="p"
                size={3}
                color="critical1"
                marginBottom={3}
                data-test-id="attic-location-form-error"
              >
                {message}
              </Text>
            ))}
            <Box display="grid" gridTemplateColumns={{ mobile: 1, desktop: 2 }} gap={4}>
              <Input
                label={intl.formatMessage(locationMessages.code)}
                value={data.code}
                disabled={disabled}
                onChange={event => change({ code: event.target.value.toUpperCase() })}
                error={!!fieldError("code")}
                helperText={fieldError("code")}
                data-test-id="attic-location-code"
              />
              <Input
                label={intl.formatMessage(locationMessages.name)}
                value={data.name}
                disabled={disabled}
                onChange={event => change({ name: event.target.value })}
                data-test-id="attic-location-name"
              />
              <Select
                label={intl.formatMessage(locationMessages.kind)}
                options={kindOptions}
                value={kindOptions.find(option => option.value === data.kind) ?? kindOptions[0]}
                disabled={disabled}
                onChange={option => option && change({ kind: option.value as AtticLocationKind })}
                data-test-id="attic-location-kind-input"
              />
              <Input
                label={intl.formatMessage(locationMessages.parent)}
                value={data.parent}
                disabled={disabled}
                onChange={event => change({ parent: event.target.value.toUpperCase() })}
                error={!!fieldError("parent")}
                helperText={fieldError("parent") ?? intl.formatMessage(locationMessages.parentHelp)}
                data-test-id="attic-location-parent"
              />
            </Box>
            <Box marginTop={4} display="flex" flexDirection="column" gap={1}>
              <Checkbox
                checked={data.isActive}
                disabled={disabled}
                onCheckedChange={checked => change({ isActive: checked === true })}
                data-test-id="attic-location-active"
              >
                <Text size={3}>{intl.formatMessage(locationMessages.isActive)}</Text>
              </Checkbox>
              <Text size={2} color="default2">
                {intl.formatMessage(locationMessages.isActiveHelp)}
              </Text>
            </Box>
            <Box marginTop={4}>
              <Input
                label={intl.formatMessage(locationMessages.notes)}
                value={data.notes}
                disabled={disabled}
                onChange={event => change({ notes: event.target.value })}
                data-test-id="attic-location-notes"
              />
            </Box>
            {location && (
              <Box marginTop={4}>
                <Text size={2} color="default2">
                  {intl.formatMessage(locationMessages.barcode)}:{" "}
                </Text>
                <Text
                  size={3}
                  style={{ fontFamily: MONO_FONT_FAMILY }}
                  data-test-id="attic-location-barcode"
                >
                  {location.barcode}
                </Text>
              </Box>
            )}
          </DashboardCard.Content>
        </DashboardCard>
        <DashboardCard data-test-id="attic-location-rules">
          <DashboardCard.Header>
            <DashboardCard.Title>{intl.formatMessage(locationMessages.rules)}</DashboardCard.Title>
          </DashboardCard.Header>
          <DashboardCard.Content>
            <Text as="p" size={2} color="default2" marginBottom={4}>
              {intl.formatMessage(locationMessages.rulesHelp)}
            </Text>
            <Box display="grid" gridTemplateColumns={{ mobile: 1, desktop: 4 }} gap={4}>
              <RulePicker
                label={intl.formatMessage(locationMessages.ruleCategories)}
                values={data.cardCategories}
                options={(choices?.categories ?? []).map(value => ({ label: value, value }))}
                disabled={disabled}
                onChange={cardCategories => change({ cardCategories })}
                testId="attic-location-rule-categories"
              />
              <RulePicker
                label={intl.formatMessage(locationMessages.ruleSets)}
                values={data.setCodes}
                options={(choices?.sets ?? []).map(set => ({
                  label: `${set.name} (${set.code})`,
                  value: set.code,
                }))}
                disabled={disabled}
                onChange={setCodes => change({ setCodes })}
                testId="attic-location-rule-sets"
              />
              <RulePicker
                label={intl.formatMessage(locationMessages.ruleRarities)}
                values={data.rarities}
                options={(choices?.rarities ?? []).map(value => ({ label: value, value }))}
                disabled={disabled}
                onChange={rarities => change({ rarities })}
                testId="attic-location-rule-rarities"
              />
              <RulePicker
                label={intl.formatMessage(locationMessages.ruleFinishes)}
                values={data.finishes}
                options={(choices?.finishes ?? []).map(value => ({
                  label: value.charAt(0).toUpperCase() + value.slice(1),
                  value,
                }))}
                disabled={disabled}
                onChange={finishes => change({ finishes })}
                testId="attic-location-rule-finishes"
              />
              <RulePicker
                label={intl.formatMessage(locationMessages.ruleTiers)}
                values={data.tiers}
                options={Object.values(AtticCardTier).map(tier => ({
                  label: intl.formatMessage(tierMessages[tier]),
                  value: tier,
                }))}
                disabled={disabled}
                onChange={tiers =>
                  change({
                    tiers: tiers.filter((tier): tier is AtticCardTier =>
                      Object.values<string>(AtticCardTier).includes(tier),
                    ),
                  })
                }
                testId="attic-location-rule-tiers"
              />
            </Box>
          </DashboardCard.Content>
        </DashboardCard>
        {location && (
          <DashboardCard data-test-id="attic-location-contents">
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(locationMessages.contents)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content paddingX={0}>
              <AtticUnitsTable
                units={location.units}
                show={{ sku: true, lot: true }}
                emptyText={intl.formatMessage(locationMessages.noContents)}
              />
            </DashboardCard.Content>
          </DashboardCard>
        )}
      </DetailPageLayout.Content>
      <DetailPageLayout.RightSidebar>
        {location && (
          <DashboardCard data-test-id="attic-location-children">
            <DashboardCard.Header>
              <DashboardCard.Title>
                {intl.formatMessage(locationMessages.children)}
              </DashboardCard.Title>
            </DashboardCard.Header>
            <DashboardCard.Content paddingX={0}>
              <ResponsiveTable>
                <TableBody>
                  {location.children.length === 0 ? (
                    <TableRowLink>
                      <TableCell>
                        <Text size={3} color="default2">
                          {intl.formatMessage(locationMessages.noChildren)}
                        </Text>
                      </TableCell>
                    </TableRowLink>
                  ) : (
                    location.children.map(child => (
                      <TableRowLink key={child.id} data-test-id="attic-location-child">
                        <TableCell>
                          <Link to={atticLocationUrl(child.code)}>
                            <Text size={3} style={{ fontFamily: MONO_FONT_FAMILY }}>
                              {child.code}
                            </Text>
                          </Link>
                        </TableCell>
                        <TableCell align="right">
                          <Text size={3} color="default2">
                            {child.unitCount}
                          </Text>
                        </TableCell>
                      </TableRowLink>
                    ))
                  )}
                </TableBody>
              </ResponsiveTable>
            </DashboardCard.Content>
          </DashboardCard>
        )}
      </DetailPageLayout.RightSidebar>
      <Savebar>
        {location && onDelete && (
          <Savebar.DeleteButton
            onClick={onDelete}
            disabled={disabled || !isLocationEmpty(location)}
            title={
              isLocationEmpty(location)
                ? undefined
                : intl.formatMessage(locationMessages.deleteBlocked)
            }
            data-test-id="attic-location-delete"
          />
        )}
        <Savebar.Spacer />
        <Savebar.CancelButton onClick={() => navigate(atticLocationListPath)} />
        <Savebar.ConfirmButton
          transitionState={saveButtonBarState}
          onClick={() => onSubmit(data)}
          disabled={disabled || data.code.trim() === ""}
          data-test-id="attic-location-save"
        />
      </Savebar>
    </DetailPageLayout>
  );
};

export default AtticLocationDetailsPage;
